/**
 * The rider on a delivery, picked from the registry.
 *
 * Three boxes — Rider ID, name, phone — and typing in any of them searches
 * the riders this location may use, by all three at once. Choosing one fills
 * every box from the registry and remembers the rider's id; typing again
 * forgets the choice. Nothing is ever chosen for the counter: two riders who
 * both match are two rows, and the person picks.
 *
 * The list is the location's whole active roster, fetched once and filtered
 * here with the same matcher the server uses, so results appear as fast as
 * somebody types and say the same thing the API would.
 */

import { useId, useMemo, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  matchRiders,
  normalizeNigerianPhone,
  phoneKey,
  type SelectableRider,
} from '@cenox/shared';
import { ApiError, api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { Button, Input } from '../../components/ui';

export interface RiderValue {
  /** The registry rider chosen. Null while nobody is chosen. */
  riderId: string | null;
  riderExternalId: string;
  riderName: string;
  riderPhone: string;
  /**
   * Set when the chosen rider was on the books without a phone and the
   * business is in compatible mode: the phone may be typed, or left blank.
   */
  phoneOptional?: boolean;
}

export const EMPTY_RIDER: RiderValue = { riderId: null, riderExternalId: '', riderName: '', riderPhone: '' };

type Field = 'riderExternalId' | 'riderName' | 'riderPhone';

/**
 * What the chosen rider's record is missing, filled on the form.
 *
 * Saved to the registry — with its audit entry — before the delivery is, by
 * somebody allowed to edit riders. A delivery never carries a phone or ID its
 * rider's record does not.
 */
export interface RiderCompletion {
  riderId: string;
  externalId?: string;
  phone?: string;
}

/** The problems with a rider choice, in the words the form shows. Empty: fine. */
export function riderProblems(
  value: RiderValue,
  opts: { required: boolean },
): Partial<Record<Field | 'riderId', string>> {
  const typed = Boolean(value.riderExternalId.trim() || value.riderName.trim() || value.riderPhone.trim());
  const out: Partial<Record<Field | 'riderId', string>> = {};
  if (!value.riderId) {
    if (typed) out.riderId = 'Choose the rider from the list';
    else if (opts.required) out.riderId = 'Choose the rider for this delivery';
    return out;
  }
  if (!value.riderExternalId.trim()) out.riderExternalId = 'Rider ID is required';
  if (!value.riderName.trim()) out.riderName = 'Rider name is required';
  if (!value.riderPhone.trim()) {
    if (!value.phoneOptional) out.riderPhone = 'Rider phone number is required';
  } else if (!normalizeNigerianPhone(value.riderPhone)) {
    out.riderPhone = 'Enter a Nigerian phone number, e.g. 08031234567';
  }
  return out;
}

export function RiderFields({
  branchId,
  value,
  onChange,
  required,
  errors,
  current,
}: {
  branchId: string;
  value: RiderValue;
  onChange: (next: RiderValue, completion: RiderCompletion | null) => void;
  required: boolean;
  errors: Partial<Record<Field | 'riderId', string>>;
  /**
   * The rider the delivery already names, when correcting one. Kept even if
   * they have since left — they are still who did that job.
   */
  current?: RiderValue | null;
}): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const listId = useId();
  // Editing riders is the roster's own permission, and never a shared counter login.
  const canEditRoster = can('deliveries:manage') && business?.role !== 'location_operator';

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [registering, setRegistering] = useState(false);

  const ridersQuery = useQuery({
    queryKey: ['delivery-riders', business?.businessId, branchId],
    queryFn: () =>
      api.get<{ data: SelectableRider[] }>(path(`/deliveries/riders?branchId=${encodeURIComponent(branchId)}`)),
    enabled: Boolean(business && branchId),
    staleTime: 60_000,
  });
  const roster = ridersQuery.data?.data ?? [];
  const matches = useMemo(() => matchRiders(roster, query).slice(0, 8), [roster, query]);
  const chosen = value.riderId ? roster.find((r) => r.id === value.riderId) ?? null : null;
  const missing = {
    externalId: Boolean(chosen && !chosen.externalId?.trim()),
    phone: Boolean(chosen && !phoneKey(chosen.phone)),
  };
  const incomplete = missing.externalId || missing.phone;
  /**
   * Who may fill which gap. Somebody who may edit riders fills it on the
   * record. Anybody may type a phone for this one delivery while the business
   * is in compatible mode, for a rider who was on the books without one.
   */
  const mayFill = {
    externalId: missing.externalId && canEditRoster,
    phone: missing.phone && (canEditRoster || Boolean(chosen?.typedPhoneAllowed)),
  };

  function completionFor(next: RiderValue, rider: SelectableRider | null): RiderCompletion | null {
    // Only somebody who may edit riders writes to the record; anybody else's
    // typed phone stays on this delivery.
    if (!rider || !next.riderId || !canEditRoster) return null;
    const c: RiderCompletion = { riderId: rider.id };
    if (!rider.externalId?.trim() && next.riderExternalId.trim()) c.externalId = next.riderExternalId.trim();
    if (!phoneKey(rider.phone) && next.riderPhone.trim()) {
      c.phone = normalizeNigerianPhone(next.riderPhone) ?? next.riderPhone.trim();
    }
    return c.externalId || c.phone ? c : null;
  }

  function choose(rider: SelectableRider): void {
    onChange(
      {
        riderId: rider.id,
        riderExternalId: rider.externalId ?? '',
        riderName: rider.name,
        riderPhone: rider.phone ?? '',
        ...(rider.typedPhoneAllowed && !phoneKey(rider.phone) ? { phoneOptional: true } : {}),
      },
      null,
    );
    setQuery('');
    setOpen(false);
  }

  /**
   * Typing into a box. A chosen rider's own details are not edited in place —
   * changing any of them means a different rider — except a detail their
   * record is missing, which is being supplied, not changed.
   */
  function type(field: Field, text: string): void {
    const fillingGap =
      chosen && ((field === 'riderExternalId' && mayFill.externalId) || (field === 'riderPhone' && mayFill.phone));
    if (fillingGap) {
      const next = { ...value, [field]: text };
      onChange(next, completionFor(next, chosen));
      return;
    }
    onChange({ ...(value.riderId ? EMPTY_RIDER : value), riderId: null, [field]: text }, null);
    setQuery(text);
    setActive(0);
    setOpen(text.trim().length > 0);
    setRegistering(false);
  }

  function onKey(event: KeyboardEvent<HTMLInputElement>): void {
    if (!open) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      // Enter picks the highlighted row the person can see — it never picks
      // a rider out of a list nobody has looked at.
      if (matches[active]) {
        event.preventDefault();
        choose(matches[active]);
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const label = (text: string) => (required ? text : `${text} (optional)`);
  const box = (field: Field, text: string, extra: Record<string, unknown> = {}) => (
    <Input
      label={label(text)}
      required={required}
      value={value[field]}
      onChange={(e) => type(field, e.target.value)}
      onKeyDown={onKey}
      onFocus={() => query.trim() && !value.riderId && setOpen(true)}
      onBlur={() => setTimeout(() => setOpen(false), 150)}
      autoComplete="off"
      role="combobox"
      aria-expanded={open}
      aria-controls={listId}
      aria-autocomplete="list"
      error={errors[field]}
      {...extra}
    />
  );

  const currentIsInactive = Boolean(value.riderId && current?.riderId === value.riderId && !chosen && ridersQuery.data);

  return (
    <div className="space-y-2">
      {/* Bottom-aligned: a label that wraps must not push its box out of line. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:items-end">
        {box('riderExternalId', 'Rider ID', { placeholder: 'e.g. CH-R006' })}
        {box('riderName', 'Rider name', { placeholder: 'Start typing a name' })}
        {box('riderPhone', 'Rider phone number', { inputMode: 'tel', placeholder: '08030000000' })}
      </div>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label="Matching riders"
          className="rounded-lg border border-slate-200 bg-white shadow-sm"
        >
          {ridersQuery.isLoading ? (
            <p className="px-3 py-2 text-sm text-slate-500">Looking…</p>
          ) : matches.length === 0 ? (
            <div className="px-3 py-2">
              <p className="text-sm font-medium text-slate-700">Rider not found</p>
              {canEditRoster ? (
                <button
                  type="button"
                  className="mt-1 text-sm font-medium text-brand-700 underline"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setRegistering(true);
                    setOpen(false);
                  }}
                >
                  Register a new rider
                </button>
              ) : (
                <p className="mt-1 text-xs text-slate-500">
                  Ask an owner or manager to add them under Riders.
                </p>
              )}
            </div>
          ) : (
            <ul>
              {matches.map((r, i) => (
                <li
                  key={r.id}
                  role="option"
                  aria-selected={i === active}
                  className={`cursor-pointer px-3 py-2 text-sm ${i === active ? 'bg-slate-100' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(r)}
                >
                  <span className="font-medium text-slate-800">{r.name}</span>
                  <span className="text-slate-500">
                    {' · '}
                    {r.externalId ?? 'no Rider ID'}
                    {' · '}
                    {r.phone ?? 'no phone'}
                  </span>
                  {!r.complete && (
                    <span className="ml-2 text-xs text-amber-700">record incomplete</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {errors.riderId && <p className="text-xs text-red-600">{errors.riderId}</p>}

      {chosen && incomplete && (
        <p role="status" className="text-xs text-amber-800">
          {chosen.name}’s record has no {missing.externalId && missing.phone ? 'Rider ID or phone number' : missing.externalId ? 'Rider ID' : 'phone number'} yet.{' '}
          {value.phoneOptional && !missing.externalId
            ? canEditRoster
              ? 'They can still be assigned. Add it above to save it to their record, or leave it blank for now.'
              : 'They can still be assigned. Type their phone above for this delivery, or leave it blank.'
            : canEditRoster
              ? 'Fill it in above — it is saved to their record when this delivery is saved.'
              : mayFill.phone && !missing.externalId
                ? 'Type their phone above for this delivery. An owner or manager can add it to their record under Riders.'
                : 'Ask an owner or manager to complete it under Riders.'}
        </p>
      )}
      {currentIsInactive && (
        <p className="text-xs text-slate-500">
          {value.riderName} is no longer an active rider. This delivery keeps them; a new rider can be chosen above.
        </p>
      )}

      {registering && (
        <RegisterRider
          initial={value}
          onCancel={() => setRegistering(false)}
          onRegistered={(rider) => {
            setRegistering(false);
            void queryClient.invalidateQueries({ queryKey: ['delivery-riders'] });
            choose(rider);
          }}
          path={path}
        />
      )}
    </div>
  );
}

/**
 * Adding a rider the registry does not have yet, without leaving the job.
 *
 * The rider directory's own endpoint — the same permission, the same
 * uniqueness rules and the same audit entry as adding them under Riders.
 */
function RegisterRider({
  initial,
  onCancel,
  onRegistered,
  path,
}: {
  initial: RiderValue;
  onCancel: () => void;
  onRegistered: (rider: SelectableRider) => void;
  path: (p: string) => string;
}): ReactNode {
  const looksLikePhone = (s: string) => /^[+\d\s()\-.]+$/.test(s.trim()) && s.trim().length > 0;
  const [form, setForm] = useState({
    canonicalName: looksLikePhone(initial.riderName) ? '' : initial.riderName,
    externalId: initial.riderExternalId,
    phone: initial.riderPhone,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: () =>
      api.post<{ data: { id: string; canonicalName: string; externalId: string | null; phone: string | null; branchId: string | null } }>(
        path('/rider-directory'),
        {
          canonicalName: form.canonicalName.trim(),
          externalId: form.externalId.trim(),
          phone: normalizeNigerianPhone(form.phone),
        },
      ),
    onSuccess: ({ data }) =>
      onRegistered({
        id: data.id,
        name: data.canonicalName,
        externalId: data.externalId,
        phone: data.phone,
        aliases: [],
        branchId: data.branchId,
        complete: true,
        typedPhoneAllowed: false,
      }),
    onError: (caught) =>
      setErrors({
        form: caught instanceof ApiError ? caught.message : 'Could not register this rider',
        ...(caught instanceof ApiError && caught.fields
          ? Object.fromEntries(Object.entries(caught.fields).map(([k, v]) => [k, v[0] ?? '']))
          : {}),
      }),
  });

  function submit(): void {
    const next: Record<string, string> = {};
    if (!form.canonicalName.trim()) next['canonicalName'] = 'Rider name is required';
    if (!form.externalId.trim()) next['externalId'] = 'Rider ID is required';
    if (!normalizeNigerianPhone(form.phone)) next['phone'] = 'Enter a Nigerian phone number, e.g. 08031234567';
    setErrors(next);
    if (Object.keys(next).length === 0) save.mutate();
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" aria-label="Register a new rider">
      <p className="mb-2 text-sm font-medium text-slate-700">Register a new rider</p>
      {errors['form'] && <p className="mb-2 text-xs text-red-600">{errors['form']}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Input
          label="Rider ID"
          required
          value={form.externalId}
          onChange={(e) => setForm((f) => ({ ...f, externalId: e.target.value }))}
          error={errors['externalId']}
        />
        <Input
          label="Rider name"
          required
          value={form.canonicalName}
          onChange={(e) => setForm((f) => ({ ...f, canonicalName: e.target.value }))}
          error={errors['canonicalName']}
        />
        <Input
          label="Rider phone number"
          required
          inputMode="tel"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          error={errors['phone']}
        />
      </div>
      <div className="mt-3 flex gap-2">
        {/* Not a submit: this sits inside the delivery's form. */}
        <Button type="button" loading={save.isPending} onClick={submit}>
          Register rider
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
